import React from 'react';
import { ArrowRight, Clock, TrendingUp, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';

export const TrainingCaseStudies = () => {
  const { language } = useLanguage();

  const caseStudies = language === 'fr' ? [
    {
      title: 'Cabinet contentieux — Paris',
      context: 'Équipe de 15 avocats, adoption IA quasi nulle avant la formation.',
      program: 'Fondamentaux + Prompt Engineering (6 semaines)',
      results: [
        { icon: Clock, text: 'Temps de recherche réduit de 35%' },
        { icon: TrendingUp, text: '90% d\'adoption de l\'IA après 3 mois' },
        { icon: FileText, text: 'Standardisation des prompts pour les notes de synthèse' },
      ],
      tag: 'Contentieux',
    },
    {
      title: 'Direction juridique — Groupe CAC 40',
      context: '25 juristes répartis sur 4 sites, besoin d\'harmoniser les pratiques.',
      program: 'Formation Cabinet sur-mesure (8 semaines)',
      results: [
        { icon: Clock, text: '4h économisées par juriste et par semaine' },
        { icon: TrendingUp, text: 'Création d\'une bibliothèque de prompts partagée' },
        { icon: FileText, text: 'Adoption homogène de l\'IA sur tous les sites' },
      ],
      tag: 'In-house',
    },
    {
      title: 'Cabinet d\'affaires — International',
      context: 'Associés et collaborateurs, besoin de se différencier par l\'IA.',
      program: 'Programme complet sur 3 mois',
      results: [
        { icon: Clock, text: 'Rédaction de contrats accélérée de 45%' },
        { icon: TrendingUp, text: 'Nouveau service d\'audit IA proposé aux clients' },
        { icon: FileText, text: 'Intégration d\'outils IA dans les workflows M&A' },
      ],
      tag: 'M&A / Corporate',
    },
  ] : [
    {
      title: 'Litigation Firm — Paris',
      context: 'Team of 15 lawyers, near-zero AI adoption before training.',
      program: 'Essentials + Prompt Engineering (6 weeks)',
      results: [
        { icon: Clock, text: 'Research time reduced by 35%' },
        { icon: TrendingUp, text: '90% AI adoption rate after 3 months' },
        { icon: FileText, text: 'Standardized prompts for legal memos' },
      ],
      tag: 'Litigation',
    },
    {
      title: 'Legal Department — CAC 40 Group',
      context: '25 in-house lawyers across 4 offices, need to harmonize practices.',
      program: 'Custom Firm-Wide Training (8 weeks)',
      results: [
        { icon: Clock, text: '4h saved per lawyer per week' },
        { icon: TrendingUp, text: 'Shared prompt library created across teams' },
        { icon: FileText, text: 'Consistent AI adoption across all offices' },
      ],
      tag: 'In-house',
    },
    {
      title: 'Business Law Firm — International',
      context: 'Partners and associates seeking AI differentiation.',
      program: 'Full program over 3 months',
      results: [
        { icon: Clock, text: 'Contract drafting accelerated by 45%' },
        { icon: TrendingUp, text: 'New AI audit service offered to clients' },
        { icon: FileText, text: 'AI tools integrated into M&A workflows' },
      ],
      tag: 'M&A / Corporate',
    },
  ];

  return (
    <section className="py-20 bg-secondary/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-foreground mb-4 text-center">
          {language === 'fr' ? 'Avocats, juristes: les résultats de nos formations IA juridique' : 'Lawyers, Counsel: Results from Our Legal AI Training'}
        </h2>
        <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
          {language === 'fr'
            ? 'Des résultats concrets mesurés chez nos clients après nos programmes de formation.'
            : 'Concrete results measured with our clients after our training programs.'}
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {caseStudies.map((cs, index) => (
            <Card key={index} className="bg-white border-border hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start mb-2">
                  <Badge variant="secondary" className="text-xs">{cs.tag}</Badge>
                </div>
                <CardTitle className="text-lg text-foreground">{cs.title}</CardTitle>
                <p className="text-sm text-muted-foreground">{cs.context}</p>
              </CardHeader>
              <CardContent>
                <p className="text-sm font-medium text-primary mb-4">
                  {language === 'fr' ? 'Programme :' : 'Program:'} {cs.program}
                </p>
                <div className="space-y-3">
                  {cs.results.map((result, ri) => (
                    <div key={ri} className="flex items-start space-x-2">
                      <result.icon className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{result.text}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
